import React, { useState, useRef, useEffect } from 'react';
import { useOpenInNewTab } from '@/hooks/useOpenInNewTab';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { FileText, Upload, Pencil, CheckCircle2, AlertCircle, ExternalLink, X, Loader2 } from 'lucide-react';
import { reportGroupService } from '@/integrations/supabase/reportGroupService';
import { timePeriodService } from '@/integrations/supabase/periodService';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/components/AuthProvider';
import { uploadFileToStaging, createDriveReport } from '@/integrations/supabase/driveUploadService';
import { getReportFileUrl, hasReportFile } from '@/integrations/supabase/reportFileService';
import type { SupabaseReport } from '@/integrations/supabase/reportService';

const BOD_EMAIL = 'ytrinh@eco-tech.vn';

const ACCEPTED_TYPES = '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx';
const MAX_SIZE_MB = 25;

function formatPeriodLabel(id: string): string {
  if (!id || id.length < 4) return id;
  const type = id[0];
  const yearSuffix = id.substring(1, 3);
  const rest = id.substring(3);
  const year = 2000 + parseInt(yearSuffix, 10);
  if (type === 'W') return `${id} — Tuần ${parseInt(rest, 10)}, ${year}`;
  if (type === 'M') return `${id} — Tháng ${parseInt(rest, 10)}, ${year}`;
  if (type === 'Q') return `${id} — Quý ${rest}, ${year}`;
  return id;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  report: SupabaseReport | null;
}

export function EditReportDialog({ open, onOpenChange, report }: Props) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();
  const isBOD = user?.email === BOD_EMAIL;

  /** File mới nằm trong Storage nên link phải ký trước khi mở, không dán href tĩnh được. */
  const { opening: openingFile, open: openFileTab } = useOpenInNewTab();
  const handleViewCurrentFile = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!report) return;
    await openFileTab(
      () => getReportFileUrl(report),
      (m) => toast({ title: 'Không mở được file', description: m, variant: 'destructive' })
    );
  };

  const [reportName, setReportName] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [selectedTeam, setSelectedTeam] = useState('');
  const [reportDate, setReportDate] = useState('');
  const [summary, setSummary] = useState('');
  const [isSummary, setIsSummary] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [success, setSuccess] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStage, setUploadStage] = useState<'idle' | 'staging' | 'processing'>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Populate form when report changes or dialog opens.
  // (3 Select nạp ở effect riêng bên dưới, sau khi option sẵn sàng — xem chú
  // thích tại effect đó.)
  useEffect(() => {
    if (open && report) {
      setReportName(report.report_name || '');
      setReportDate(
        report.report_date
          ? report.report_date.substring(0, 10)
          : report.created_on
          ? report.created_on.substring(0, 10)
          : new Date().toISOString().substring(0, 10)
      );
      setSummary(report.summary || '');
      setIsSummary(report.is_summary === true);
      setFile(null);
      setFileError('');
      setFieldErrors({});
      setSuccess(false);
      setSaveError(null);
      setUploadProgress(0);
      setUploadStage('idle');
    }
  }, [open, report]);

  const clearFieldError = (key: string) => {
    setFieldErrors(prev => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  // Fetch report groups
  const { data: groupsData } = useQuery({
    queryKey: ['report-groups-for-edit'],
    queryFn: () => reportGroupService.getReportGroups({}, { limit: 100 }),
    staleTime: 5 * 60 * 1000,
    enabled: open,
  });

  // Fetch all periods (sorted descending so latest appear first)
  const { data: periods } = useQuery({
    queryKey: ['time-periods-all'],
    queryFn: () => timePeriodService.getTimePeriods(),
    staleTime: 5 * 60 * 1000,
    enabled: open,
    select: (data) => [...data].sort((a, b) => b.id.localeCompare(a.id)),
  });

  // Fetch teams
  const { data: teamsData } = useQuery({
    queryKey: ['teams-for-edit'],
    queryFn: async () => {
      const { data } = await supabase
        .from('teams')
        .select('legacy_id, team_name')
        .order('team_name')
        .limit(100);
      return data || [];
    },
    staleTime: 5 * 60 * 1000,
    enabled: open,
  });

  // RIÊNG 3 Select (nhóm/kỳ/team) phải đợi option nạp xong mới set giá trị.
  // Lý do: Select nằm trong <form> nên Radix render thêm một <select> native ẩn
  // (BubbleSelect). Set value khi danh sách option còn rỗng (lần mở đầu — query
  // chưa về), native select ép value thành '' và dispatch 'change' → Radix gọi
  // onValueChange('') → state vừa nạp bị XOÁ, ô hiện lại placeholder. Lần mở
  // sau trúng cache 5 phút, option mount cùng render nên không sao — vì thế
  // lỗi chỉ xảy ra ở lần mở đầu. Dep là boolean, không phải object data, để
  // refetch nền không ghi đè lựa chọn người dùng đang sửa dở.
  const selectOptionsReady = !!groupsData && !!periods && !!teamsData;
  useEffect(() => {
    if (open && report && selectOptionsReady) {
      setSelectedGroup(report.id_group || '');
      setSelectedPeriod(report.period || '');
      setSelectedTeam(report.id_team || '');
    }
  }, [open, report, selectOptionsReady]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    setFileError('');
    if (!f) { setFile(null); return; }
    if (f.size > MAX_SIZE_MB * 1024 * 1024) {
      setFileError(`File vượt quá ${MAX_SIZE_MB}MB`);
      setFile(null);
      return;
    }
    setFile(f);
  };

  const validateRequired = (): boolean => {
    const next: Record<string, string> = {};
    if (!reportName.trim()) next.reportName = 'Vui lòng nhập tên báo cáo';
    if (!selectedGroup) next.group = 'Vui lòng chọn nhóm báo cáo';
    if (!selectedPeriod) next.period = 'Vui lòng chọn kỳ báo cáo';
    if (!selectedTeam) next.team = 'Vui lòng chọn team';
    if (!reportDate) next.reportDate = 'Vui lòng chọn ngày báo cáo';
    setFieldErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!report || !validateRequired()) return;

    setIsSaving(true);
    setSaveError(null);

    try {
      if (file) {
        // Upload new file → update record
        setUploadStage('staging');
        setUploadProgress(10);

        const progressInterval = setInterval(() => {
          setUploadProgress(prev => (prev < 60 ? prev + 5 : prev));
        }, 300);

        let storagePath: string;
        try {
          storagePath = await uploadFileToStaging(file);
        } finally {
          clearInterval(progressInterval);
        }

        setUploadProgress(65);
        setUploadStage('processing');

        const edgeInterval = setInterval(() => {
          setUploadProgress(prev => (prev < 93 ? prev + 2 : prev));
        }, 500);

        try {
          await createDriveReport(storagePath, file.name, file.type, {
            report_id: report.id,
            report_name: reportName.trim(),
            id_group: selectedGroup,
            id_team: selectedTeam,
            period: selectedPeriod,
            report_date: reportDate,
            summary: summary.trim() || undefined,
            auth_user_id: report.auth_user_id,
            user_id: report.user_id,
            created_by_user_id: report.created_by_user_id,
            is_summary: isBOD ? isSummary : undefined,
          });
        } finally {
          clearInterval(edgeInterval);
        }

        setUploadProgress(100);
      } else {
        // Metadata-only update
        const updateData: Record<string, any> = {
            report_name: reportName.trim(),
            id_group: selectedGroup || null,
            period: selectedPeriod || null,
            id_team: selectedTeam || null,
            report_date: reportDate ? new Date(reportDate).toISOString() : null,
            summary: summary.trim() || null,
            updated_at: new Date().toISOString(),
        };
        if (isBOD) updateData.is_summary = isSummary;

        const { error } = await supabase
          .from('reports')
          .update(updateData)
          .eq('id', report.id);

        if (error) throw new Error(error.message);
      }

      // Invalidate caches
      queryClient.invalidateQueries({ queryKey: ['my-reports'] });
      queryClient.invalidateQueries({ queryKey: ['my-periods'] });
      queryClient.invalidateQueries({ queryKey: ['supabase-reports'] });

      toast({ title: 'Cập nhật báo cáo thành công' });
      setSuccess(true);
      setTimeout(() => handleClose(), 1500);
    } catch (err: any) {
      setSaveError(err.message || 'Có lỗi xảy ra, vui lòng thử lại');
    } finally {
      setIsSaving(false);
      setUploadStage('idle');
    }
  };

  const handleClose = () => {
    if (isSaving) return;
    setFile(null);
    setFileError('');
    setFieldErrors({});
    setSuccess(false);
    setSaveError(null);
    setUploadProgress(0);
    setUploadStage('idle');
    onOpenChange(false);
  };

  const stageLabel = uploadStage === 'staging'
    ? 'Đang tải file lên...'
    : uploadStage === 'processing'
    ? 'Đang lưu vào kho báo cáo...'
    : 'Đang lưu...';

  return (
    <Dialog open={open} onOpenChange={(next) => !next && handleClose()}>
      <DialogContent className="flex max-h-[85dvh] w-full max-w-lg flex-col gap-0 overflow-hidden p-0 sm:max-w-[32rem]">
        <div className="shrink-0 border-b px-6 pb-4 pt-6">
          <DialogHeader className="space-y-0">
            <DialogTitle className="flex items-center gap-2 pr-8">
              <Pencil className="h-5 w-5 shrink-0" />
              Chỉnh sửa báo cáo
            </DialogTitle>
          </DialogHeader>
        </div>

        {success ? (
          <>
            <div className="min-h-0 flex-1 overflow-y-auto px-6 py-8">
              <div className="flex flex-col items-center gap-3 text-center">
                <CheckCircle2 className="h-12 w-12 text-green-500" />
                <p className="text-lg font-semibold">Cập nhật thành công!</p>
              </div>
            </div>
            <DialogFooter className="shrink-0 gap-2 border-t bg-background px-6 py-4">
              <Button type="button" className="w-full sm:w-auto" onClick={handleClose}>
                Đóng
              </Button>
            </DialogFooter>
          </>
        ) : (
          <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
            <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
              <div className="space-y-4">
                {/* Report name */}
                <div className="space-y-1">
                  <Label htmlFor="edit_report_name">Tên báo cáo *</Label>
                  <Input
                    id="edit_report_name"
                    value={reportName}
                    onChange={e => { setReportName(e.target.value); clearFieldError('reportName'); }}
                    placeholder="Nhập tên báo cáo"
                    disabled={isSaving}
                    aria-invalid={!!fieldErrors.reportName}
                  />
                  {fieldErrors.reportName && <p className="text-sm text-destructive">{fieldErrors.reportName}</p>}
                </div>

                {/* Group + Period */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label>Nhóm báo cáo *</Label>
                    <Select
                      value={selectedGroup || undefined}
                      onValueChange={v => { setSelectedGroup(v); clearFieldError('group'); }}
                      disabled={isSaving}
                    >
                      <SelectTrigger aria-invalid={!!fieldErrors.group}>
                        {/* Children tường minh: lần mở đầu options chưa nạp xong
                            (query enabled: open) nên SelectItem khớp value chưa
                            mount → Radix để trống. Tự render nhãn thì không còn
                            phụ thuộc timing đó. */}
                        <SelectValue placeholder="Chọn nhóm...">
                          {selectedGroup
                            ? groupsData?.data.find(g => g.legacy_id === selectedGroup)?.group_name ?? selectedGroup
                            : null}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent className="max-h-56">
                        {groupsData?.data.map(g => (
                          <SelectItem key={g.legacy_id} value={g.legacy_id}>
                            {g.group_name || g.legacy_id}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {fieldErrors.group && <p className="text-sm text-destructive">{fieldErrors.group}</p>}
                  </div>

                  <div className="space-y-1">
                    <Label>Kỳ báo cáo *</Label>
                    <Select
                      value={selectedPeriod || undefined}
                      onValueChange={v => { setSelectedPeriod(v); clearFieldError('period'); }}
                      disabled={isSaving}
                    >
                      <SelectTrigger aria-invalid={!!fieldErrors.period}>
                        <SelectValue placeholder="Chọn kỳ...">
                          {selectedPeriod ? formatPeriodLabel(selectedPeriod) : null}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent className="max-h-72">
                        {/* Show current period first if not in list */}
                        {periods && report?.period && !periods.find(p => p.id === report.period) && (
                          <SelectItem key={report.period} value={report.period}>
                            {formatPeriodLabel(report.period)}
                          </SelectItem>
                        )}
                        {periods?.map(p => (
                          <SelectItem key={p.id} value={p.id}>
                            {formatPeriodLabel(p.id)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {fieldErrors.period && <p className="text-sm text-destructive">{fieldErrors.period}</p>}
                  </div>
                </div>

                {/* Team + Date */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label>Team *</Label>
                    <Select
                      value={selectedTeam || undefined}
                      onValueChange={v => { setSelectedTeam(v); clearFieldError('team'); }}
                      disabled={isSaving}
                    >
                      <SelectTrigger aria-invalid={!!fieldErrors.team}>
                        <SelectValue placeholder="Chọn team...">
                          {selectedTeam
                            ? teamsData?.find(t => t.legacy_id === selectedTeam)?.team_name ?? selectedTeam
                            : null}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent className="max-h-56">
                        {teamsData?.map(t => (
                          <SelectItem key={t.legacy_id} value={t.legacy_id}>
                            {t.team_name || t.legacy_id}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {fieldErrors.team && <p className="text-sm text-destructive">{fieldErrors.team}</p>}
                  </div>

                  <div className="space-y-1">
                    <Label htmlFor="edit_report_date">Ngày báo cáo *</Label>
                    <Input
                      id="edit_report_date"
                      type="date"
                      value={reportDate}
                      onChange={e => { setReportDate(e.target.value); clearFieldError('reportDate'); }}
                      disabled={isSaving}
                      aria-invalid={!!fieldErrors.reportDate}
                    />
                    {fieldErrors.reportDate && <p className="text-sm text-destructive">{fieldErrors.reportDate}</p>}
                  </div>
                </div>

                {/* Summary */}
                <div className="space-y-1">
                  <Label htmlFor="edit_summary">Tóm tắt nội dung (tùy chọn)</Label>
                  <Textarea
                    id="edit_summary"
                    value={summary}
                    onChange={e => setSummary(e.target.value)}
                    placeholder="Mô tả ngắn về nội dung báo cáo..."
                    rows={3}
                    disabled={isSaving}
                  />
                </div>

                {/* Summary report checkbox - BOD only */}
                {isBOD && (
                  <div className="flex items-center gap-2 rounded-md border bg-amber-50/60 px-3 py-2.5">
                    <Checkbox
                      id="edit_is_summary"
                      checked={isSummary}
                      onCheckedChange={(v) => setIsSummary(v === true)}
                      disabled={isSaving}
                    />
                    <Label htmlFor="edit_is_summary" className="cursor-pointer text-sm font-medium text-amber-800">
                      Đánh dấu là Báo cáo tổng hợp (Summary Report)
                    </Label>
                  </div>
                )}

                {/* File section */}
                <div className="space-y-2">
                  <Label>File báo cáo</Label>

                  {/* Current file link */}
                  {report && hasReportFile(report) && !file && (
                    <div className="flex items-center gap-2 rounded-md border bg-muted/40 px-3 py-2">
                      <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <span className="flex-1 truncate text-sm text-muted-foreground">File hiện tại</span>
                      <button
                        type="button"
                        className="flex items-center gap-1 text-xs text-blue-600 hover:underline"
                        onClick={handleViewCurrentFile}
                        disabled={openingFile}
                      >
                        {openingFile ? <Loader2 className="h-3 w-3 animate-spin" /> : <ExternalLink className="h-3 w-3" />}
                        {openingFile ? 'Đang mở…' : 'Xem'}
                      </button>
                    </div>
                  )}

                  {/* New file selected */}
                  {file && (
                    <div className="flex items-center gap-2 rounded-md border border-primary/30 bg-primary/5 px-3 py-2">
                      <FileText className="h-4 w-4 shrink-0 text-primary" />
                      <span className="flex-1 truncate text-sm font-medium">{file.name}</span>
                      <span className="text-xs text-muted-foreground">({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={() => { setFile(null); setFileError(''); }}
                        disabled={isSaving}
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  )}

                  {/* Upload zone */}
                  <div
                    role="button"
                    tabIndex={0}
                    onKeyDown={e => {
                      if (!isSaving && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault();
                        fileInputRef.current?.click();
                      }
                    }}
                    className="cursor-pointer rounded-lg border-2 border-dashed p-3 text-center transition-colors hover:border-primary/60 hover:bg-accent/30"
                    onClick={() => !isSaving && fileInputRef.current?.click()}
                  >
                    <div className="flex flex-col items-center gap-1 text-muted-foreground">
                      <Upload className="h-5 w-5" />
                      <span className="text-sm">{file ? 'Chọn file khác' : 'Thay thế file (tùy chọn)'}</span>
                      <span className="text-xs">.pdf, .doc, .docx, .xls, .xlsx, .ppt, .pptx — tối đa {MAX_SIZE_MB}MB</span>
                    </div>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={ACCEPTED_TYPES}
                    className="hidden"
                    onChange={handleFileChange}
                    disabled={isSaving}
                  />
                  {fileError && <p className="text-sm text-destructive">{fileError}</p>}
                  {file && (
                    <Badge variant="secondary" className="text-xs">
                      File mới sẽ được lưu vào kho báo cáo
                    </Badge>
                  )}
                </div>

                {/* Upload progress */}
                {isSaving && file && (
                  <div className="space-y-1">
                    <div className="flex justify-between text-sm text-muted-foreground">
                      <span>{stageLabel}</span>
                      <span>{uploadProgress}%</span>
                    </div>
                    <Progress value={uploadProgress} />
                  </div>
                )}

                {/* Error */}
                {saveError && !isSaving && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{saveError}</AlertDescription>
                  </Alert>
                )}
              </div>
            </div>

            <DialogFooter className="shrink-0 gap-2 border-t bg-background px-6 py-4">
              <Button type="button" variant="outline" onClick={handleClose} disabled={isSaving}>
                Hủy
              </Button>
              <Button type="submit" disabled={isSaving}>
                {isSaving ? stageLabel : 'Lưu thay đổi'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
